/**
 * kbApi.ts — TanStack Query hooks for the Knowledge Base backend
 * (wiki-llm-service, B1a — live). This is the first surface wired to a real
 * backend (B1b Step 1). Today it covers the document LIST read; upload + review
 * decisions are the next slice and still run through the mock kbStore.
 *
 * Maps the backend wire shapes (api/schemas.py) onto the frontend KbDoc model
 * (mock.ts), absorbing two convention mismatches found during integration:
 *   - the backend lowercases the `domain` value; the UI's domain config keys
 *     are mixed-case ("HR", "Procurement"), so we resolve case-insensitively.
 *   - status uses an underscore (`pending_review`) vs the UI's hyphen
 *     (`pending-review`).
 * Known gap: the list endpoint's DocumentSummary carries NO chunk count, so
 * `chunks` is 0 until the backend adds `chunk_count` to DocumentSummary (a B1a
 * follow-up tracked in Frontend_action_contract.md / B1b_backend_build_plan.md).
 */
import { useAuth0 } from "@auth0/auth0-react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { ApiError, useApi } from "./api";
import {
  MOCK_KB_DOMAINS,
  type KbDecision,
  type KbDoc,
  type KbDocStatus,
  type KbLintFinding,
  type KbReview,
  type KbWikiEdge,
  type KbWikiPage,
  type LintCategory,
  type WikiPageType,
} from "./mock";

/** Wire shape of DocumentSummary (api/schemas.py). */
interface DocumentSummaryDto {
  id: string;
  title: string;
  original_filename: string;
  domain: string | null;
  status: string;
  uploaded_by_sub: string;
  created_at: string;
  updated_at: string;
  /** Not yet returned by the list endpoint — see "Known gap" above. */
  chunk_count?: number;
  /** Live synthesis stage while status === "ingesting" (drives the progress UI). */
  synthesis_stage?: string | null;
}

interface DocumentListDto {
  documents: DocumentSummaryDto[];
  total: number;
  limit: number;
  offset: number;
}

const STATUS_MAP: Record<string, KbDocStatus> = {
  ingesting: "ingesting",
  live: "live",
  pending_review: "pending-review",
  rejected: "rejected",
  archived: "archived",
};

function resolveDomainKey(raw: string | null): string {
  if (!raw) return "Altro";
  const hit = MOCK_KB_DOMAINS.find((d) => d.key.toLowerCase() === raw.toLowerCase());
  return hit ? hit.key : raw;
}

function toKbDoc(dto: DocumentSummaryDto): KbDoc {
  return {
    id: dto.id,
    filename: dto.original_filename,
    domain: resolveDomainKey(dto.domain),
    uploader: dto.uploaded_by_sub,
    date: dto.created_at.slice(0, 10),
    chunks: dto.chunk_count ?? 0,
    status: STATUS_MAP[dto.status] ?? "ingesting",
    synthesisStage: dto.synthesis_stage ?? null,
  };
}

/** Live document list for the active tenant (RLS-scoped server-side). Polls
 * every 2.5s WHILE any doc is still ingesting, so the synthesis progress (and
 * the doc flipping to "live") updates on its own — no manual refresh. */
export function useKbDocuments(): UseQueryResult<KbDoc[]> {
  const api = useApi();
  return useQuery<KbDoc[]>({
    queryKey: ["kb", "documents"],
    refetchInterval: (query) =>
      (query.state.data ?? []).some((d) => d.status === "ingesting") ? 2500 : false,
    queryFn: async ({ signal }): Promise<KbDoc[]> => {
      const res = await api.wikiLLM.get<DocumentListDto>("/v1/documents", { signal });
      return res.documents.map(toKbDoc);
    },
  });
}

/** Wire shape of DocumentCreated (POST /v1/documents response). */
interface DocumentCreatedDto {
  id: string;
  status: string;
  title: string;
  original_filename: string;
  domain: string | null;
  created_at: string;
}

const BACKEND_DOMAINS = new Set([
  "hr",
  "it",
  "procurement",
  "product",
  "reference",
  "other",
  "project",
]);

/**
 * Map a frontend domain key (mixed-case, a superset — e.g. "Altro", "Project")
 * to a backend-accepted domain. The backend only allows the six in
 * BACKEND_DOMAINS (api/documents.py); anything else folds into "other".
 */
function toBackendDomain(key: string | undefined): string | undefined {
  if (!key) return undefined;
  const k = key.trim().toLowerCase();
  return BACKEND_DOMAINS.has(k) ? k : "other";
}

export interface KbUploadInput {
  file: File;
  title?: string;
  domain?: string;
}

/** Upload a document to the KB (multipart → POST /v1/documents). Refetches the list on success. */
export function useUploadKbDocument(): UseMutationResult<DocumentCreatedDto, Error, KbUploadInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, title, domain }: KbUploadInput): Promise<DocumentCreatedDto> => {
      const form = new FormData();
      form.append("file", file);
      if (title) form.append("title", title);
      const backendDomain = toBackendDomain(domain);
      if (backendDomain) form.append("domain", backendDomain);
      return api.wikiLLM.post<DocumentCreatedDto>("/v1/documents", { formData: form });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "documents"] });
    },
  });
}

/** Soft-delete a KB document (DELETE /v1/documents/{id} → status archived). Refetches the list. */
export function useDeleteKbDocument(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.wikiLLM.del<void>(`/v1/documents/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "documents"] });
    },
  });
}

// ── Graph / chat / reviews / lint (B1b synthesis surfaces) ──────────────────

const ROLE_CLAIM = "https://cme-studio.app/role";

/** Reviews + lint are admin-only; gate the calls so non-admins don't 403-loop. */
export function useIsKbAdmin(): boolean {
  const { user } = useAuth0();
  return user?.[ROLE_CLAIM] === "admin";
}

/** Don't retry admin-only 401/403s (the global retry:1 would otherwise re-hit). */
function adminRetry(count: number, err: Error): boolean {
  if (err instanceof ApiError && (err.status === 401 || err.status === 403)) return false;
  return count < 1;
}

// --- graph ---
interface GraphNodeDto {
  id: string;
  title: string;
  type: WikiPageType;
  domain: string | null;
  chunks: number;
  sourceFileIds: string[];
  pageRefs: string[];
  status: string;
}
interface GraphDto {
  nodes: GraphNodeDto[];
  edges: { from: string; to: string; type: "ref" | "sim" }[];
}

export interface KbGraph {
  pages: KbWikiPage[];
  edges: KbWikiEdge[];
}

/** `pollWhileBusy` polls the graph every 3s (used while a doc is synthesizing,
 * so freshly-written pages appear in Esplora without a manual reload). */
export function useKbGraph(opts?: { pollWhileBusy?: boolean }): UseQueryResult<KbGraph> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "graph"],
    refetchInterval: opts?.pollWhileBusy ? 3000 : false,
    queryFn: async ({ signal }): Promise<KbGraph> => {
      const res = await api.wikiLLM.get<GraphDto>("/v1/graph", { signal });
      return {
        pages: res.nodes.map((n) => ({
          id: n.id,
          title: n.title,
          type: n.type,
          domain: resolveDomainKey(n.domain),
          chunks: n.chunks,
          sourceFileIds: n.sourceFileIds,
          pageRefs: n.pageRefs,
          status: STATUS_MAP[n.status] ?? "live",
        })),
        edges: res.edges.map((e) => ({ from: e.from, to: e.to, type: e.type })),
      };
    },
  });
}

// --- page detail (reading view) ---
export interface KbPageSource {
  documentId: string;
  filename: string;
  url: string;
}
export interface KbPageDetail {
  id: string;
  title: string;
  type: WikiPageType;
  domain: string;
  status: KbDocStatus;
  properties: Record<string, unknown>;
  bodyMarkdown: string;
  sources: KbPageSource[];
}
interface WikiPageDetailDto {
  id: string;
  title: string;
  type: WikiPageType;
  domain: string | null;
  status: string;
  properties: Record<string, unknown>;
  body_markdown: string;
  sources: { document_id: string; filename: string; url: string }[];
}

/** Fetch one synthesized page's display properties + rendered Markdown body. */
export function useKbPage(pageId: string | null): UseQueryResult<KbPageDetail> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "page", pageId],
    enabled: !!pageId,
    queryFn: async ({ signal }): Promise<KbPageDetail> => {
      const dto = await api.wikiLLM.get<WikiPageDetailDto>(`/v1/pages/${pageId}`, { signal });
      return {
        id: dto.id,
        title: dto.title,
        type: dto.type,
        domain: resolveDomainKey(dto.domain),
        status: STATUS_MAP[dto.status] ?? "live",
        properties: dto.properties,
        bodyMarkdown: dto.body_markdown,
        sources: dto.sources.map((s) => ({
          documentId: s.document_id,
          filename: s.filename,
          url: s.url,
        })),
      };
    },
  });
}

// --- chat / query ---
export interface KbQueryResult {
  answer: string;
  citations: { pageId: string; title: string; snippet: string; score: number }[];
}
export interface KbQueryInput {
  question: string;
  domain?: string;
  topK?: number;
}

export function useKbQuery(): UseMutationResult<KbQueryResult, Error, KbQueryInput> {
  const api = useApi();
  return useMutation({
    mutationFn: ({ question, domain, topK }: KbQueryInput): Promise<KbQueryResult> =>
      api.wikiLLM.post<KbQueryResult>("/v1/query", {
        body: { question, domain, top_k: topK ?? 5 },
      }),
  });
}

// --- save a chat answer back to the KB as an "Analisi" wiki page (Query step 5) ---
export interface SaveAnalysisInput {
  question: string;
  answer: string;
  citationPageIds: string[];
}
export interface AnalysisPageCreated {
  id: string;
  title: string;
  slug: string;
  type: WikiPageType;
  chunkCount: number;
}
interface AnalysisPageCreatedDto {
  id: string;
  title: string;
  slug: string;
  type: WikiPageType;
  chunk_count: number;
}

/** Persist a chat answer as an embedded `analysis` page; refreshes the graph so
 * the new purple node + its [[ref]] edges appear. */
export function useSaveAnalysis(): UseMutationResult<
  AnalysisPageCreated,
  Error,
  SaveAnalysisInput
> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      question,
      answer,
      citationPageIds,
    }: SaveAnalysisInput): Promise<AnalysisPageCreated> => {
      const dto = await api.wikiLLM.post<AnalysisPageCreatedDto>("/v1/pages/analysis", {
        body: { question, answer, citation_page_ids: citationPageIds },
      });
      return {
        id: dto.id,
        title: dto.title,
        slug: dto.slug,
        type: dto.type,
        chunkCount: dto.chunk_count,
      };
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
    },
  });
}

// --- rename / delete a wiki page (left-rail actions) ---
export interface RenameKbPageInput {
  id: string;
  title: string;
}

/** Rename a wiki page (PATCH /v1/pages/{id}). Slug + edges stay stable. */
export function useRenameKbPage(): UseMutationResult<void, Error, RenameKbPageInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, title }: RenameKbPageInput): Promise<void> => {
      await api.wikiLLM.patch<WikiPageDetailDto>(`/v1/pages/${id}`, { body: { title } });
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
      void qc.invalidateQueries({ queryKey: ["kb", "page", id] });
    },
  });
}

export interface SaveKbPageBodyInput {
  id: string;
  bodyMarkdown: string;
}

/** Save an edited page body (PATCH /v1/pages/{id} with body_markdown). For an
 * Analisi page the backend also re-embeds it so future queries reflect the edit. */
export function useSaveKbPageBody(): UseMutationResult<void, Error, SaveKbPageBodyInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, bodyMarkdown }: SaveKbPageBodyInput): Promise<void> => {
      await api.wikiLLM.patch<WikiPageDetailDto>(`/v1/pages/${id}`, {
        body: { body_markdown: bodyMarkdown },
      });
    },
    onSuccess: (_data, { id }) => {
      void qc.invalidateQueries({ queryKey: ["kb", "page", id] });
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
    },
  });
}

/** Delete a wiki page (DELETE /v1/pages/{id}). For an Analisi page this also
 * removes its embedded chunks from future queries (backend cascades). */
export function useDeleteKbPage(): UseMutationResult<void, Error, string> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string): Promise<void> => api.wikiLLM.del<void>(`/v1/pages/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
    },
  });
}

// --- reviews ---
export const BE_TO_FE_DECISION: Record<string, KbDecision> = {
  accept_new: "accept_new",
  keep_existing: "reject_new",
  merge: "merge",
  clarify: "clarification",
};
export const FE_TO_BE_DECISION: Record<KbDecision, string> = {
  accept_new: "accept_new",
  reject_new: "keep_existing",
  merge: "merge",
  clarification: "clarify",
};
const BE_STATUS_TO_DECISION: Record<string, KbDecision | undefined> = {
  accepted: "accept_new",
  rejected: "reject_new",
  merged: "merge",
  clarified: "clarification",
};

interface ReviewItemDto {
  id: string;
  new_document_id: string;
  new_document_title: string;
  domain: string | null;
  uploader_sub: string;
  ai_recommendation: string;
  ai_reasoning: string;
  confidence: number;
  new_snippet: string;
  conflicting_snippet: string;
  status: string;
  decided_by_sub: string | null;
  decided_at: string | null;
  created_at: string;
}

function toReview(it: ReviewItemDto): KbReview {
  return {
    id: it.id,
    newDocId: it.new_document_id,
    existingDocId: null,
    domain: resolveDomainKey(it.domain),
    uploader: it.uploader_sub,
    uploadedAt: it.created_at.slice(0, 16).replace("T", " "),
    confidence: it.confidence,
    newSnippet: it.new_snippet,
    existingSnippet: it.conflicting_snippet || null,
    existingDocSource: null,
    newDocSource: it.new_document_title,
    aiReasoning: it.ai_reasoning,
    aiRecommendation: BE_TO_FE_DECISION[it.ai_recommendation] ?? "clarification",
    decidedAt: it.decided_at ? it.decided_at.slice(0, 16).replace("T", " ") : undefined,
    decidedBy: it.decided_by_sub ?? undefined,
    decision: BE_STATUS_TO_DECISION[it.status],
  };
}

export function useKbReviews(opts?: {
  decided?: boolean;
  enabled?: boolean;
}): UseQueryResult<KbReview[]> {
  const api = useApi();
  const decided = opts?.decided ?? false;
  return useQuery({
    queryKey: ["kb", "reviews", decided ? "decided" : "pending"],
    enabled: opts?.enabled ?? true,
    retry: adminRetry,
    queryFn: async ({ signal }): Promise<KbReview[]> => {
      const res = await api.wikiLLM.get<{ items: ReviewItemDto[]; total: number }>("/v1/reviews", {
        signal,
        params: { decided },
      });
      return res.items.map(toReview);
    },
  });
}

export interface DecideReviewInput {
  id: string;
  decision: KbDecision;
  note?: string;
}

export function useDecideReview(): UseMutationResult<void, Error, DecideReviewInput> {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision, note }: DecideReviewInput): Promise<void> =>
      api.wikiLLM.post<void>(`/v1/reviews/${id}/decide`, {
        body: { decision: FE_TO_BE_DECISION[decision], note },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["kb", "reviews"] });
      void qc.invalidateQueries({ queryKey: ["kb", "documents"] });
      void qc.invalidateQueries({ queryKey: ["kb", "graph"] });
    },
  });
}

// --- lint ---
interface LintFindingDto {
  kind: string;
  target_type: string;
  target_id: string;
  detail: string;
  linked_review_id: string | null;
}
export const LINT_KIND_TO_CATEGORY: Record<string, LintCategory> = {
  orphan_no_chunks: "orphan",
  orphan: "orphan",
  contradiction: "contradiction",
  stale: "stale",
  missing_cross_reference: "missing-link",
};

function toLintFinding(f: LintFindingDto): KbLintFinding {
  const category = LINT_KIND_TO_CATEGORY[f.kind] ?? "data-gap";
  return {
    id: `${f.kind}:${f.target_id}`,
    category,
    severity: category === "contradiction" || category === "stale" ? "warn" : "info",
    title: f.kind.replace(/_/g, " "),
    summary: f.detail,
    suggestion: "",
    pageA: ["page", "document"].includes(f.target_type) ? f.target_id : undefined,
    linkedReviewId: f.linked_review_id ?? undefined,
  };
}

export function useKbLint(opts?: { enabled?: boolean }): UseQueryResult<KbLintFinding[]> {
  const api = useApi();
  return useQuery({
    queryKey: ["kb", "lint"],
    enabled: opts?.enabled ?? true,
    retry: adminRetry,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<KbLintFinding[]> => {
      const res = await api.wikiLLM.post<{ findings: LintFindingDto[] }>("/v1/lint", {});
      return res.findings.map(toLintFinding);
    },
  });
}
